import { Character, CharacterChatHistory, CharacterMetadata } from '@/app/lib/types/character';
import { loadCharacterMetadataAction, loadCharacterChatHistoryAction } from '@/app/lib/actions';
import { getPresignedUrl } from '@/app/lib/actions';
import { LiveImageStatus, ImageStatusChangeCallback, registerImageStatusCallback } from './liveMessageData/liveMessage';
// import { PageParams } from '../types/chat.types';
import { extractPublicId, extractS3KeyFromImageUri } from './util';
import { liveMessageDataHub } from './liveMessageData/liveMessageDataHub';
import { Room } from 'livekit-client';
import { LocalUserChoices } from '@livekit/components-react';
import { ConnectionDetails } from '@/lib/types';
import { UserChatSetting, ImageGenMode } from './chatSetting/settingOptions';
import { userAppData } from '@/app/user-and-session/user-app-data';
import { createCloudinaryImageSync, initCloudinaryWithPreloadedConfig, isCloudinaryReady, preloadCloudinary } from '@/app/lib/cloudinary/cloudinary-image-utils';

/**
 * Interface for character loading result states
 */
export interface CharacterLoadingState {
  isCharacterLoading: boolean;
  characterLoadingError: string | null;
}

/**
 * Interface for active image data (used for left panel image)
 */
export interface ImageWithStatus {
  imageUri: string | null; // Still S3 key with extension. Easy to extract public id for Cloudinary.
  imageData: any | null; // Cloudinary image object
  imageRawData: HTMLImageElement | null; // Raw image data, not often used, for backward compatibility.
  loadingStatus: LiveImageStatus;
}

/**
 * Chat status enum - represents the current status of the chat
 */
export enum ChatStatus {
  INFO, // INFO is now blended with TEXT
  LOADING, // loading live video. Text has no loading status.
  LIVE, // Video on , voice is also on.
  Call  // Voice on only.
}

/**
 * Callback type for state change notifications
 */
export type StateChangeCallback = () => void;

/**
 * GlobalChatState singleton manages all chat-related state centrally
 */
export class GlobalChatState {
  private static instance: GlobalChatState;
  
  // Character data
  private character: Character | null = null;
  
  // Character loading states
  private characterLoadingState: CharacterLoadingState = {
    isCharacterLoading: false,
    characterLoadingError: null
  };
  
  // image management 
  // (for left panel)
  private activeImage: ImageWithStatus = {
    imageUri: null,
    imageData: null,
    imageRawData: null,
    loadingStatus: LiveImageStatus.NOT_LOADED
  };
  // (for profile header & desktop background)
  private profileImage: ImageWithStatus = {
    imageUri: null,
    imageData: null,
    imageRawData: null,
    loadingStatus: LiveImageStatus.NOT_LOADED
  };
  
  // Live chat status
  private chatStatus: ChatStatus = ChatStatus.INFO;

  // IF True, PLAY BACK WILL BE ENABLED.
  private debugPlayBack: boolean = false;
  private debugAutoLaunchVideo: boolean = false;
  
  // Chat UI state variables from TextChat
  private isImageGenerating: boolean = false;
  private streamingMessageId: string = '';
  private lastProcessedIndexForTranscriptionTracking: number = -1;

  // Livekit room state
  private room: Room | null = null;
  private preJoinChoices: LocalUserChoices | undefined = undefined;
  private connectionDetails: ConnectionDetails | undefined = undefined;
  private isInitiatingRoom: boolean = false;

  // Inactivity timeout state
  private inactivityTimeoutState: {
    isInactivityTimeoutPopupOpen: boolean;
    inactivityTimeoutReason: 'server_signal' | 'user_inactivity' | null;
  } = {
    isInactivityTimeoutPopupOpen: false,
    inactivityTimeoutReason: null
  };

  // Inactivity timer management
  private inactivityTimeoutRef: NodeJS.Timeout | null = null;
  private inactivityCountdownRef: NodeJS.Timeout | null = null;
  private readonly INACTIVITY_TIMEOUT_MS = 600_000; // 10 minutes
  private inactivityStartTime: number = 0;

  // Agent avatar signal tracking
  private agentAvatarEntered: boolean = false;
  // Agent ASR signal tracking
  private agentAsrEntered: boolean = false;

  // User chat settings
  private userChatSetting: UserChatSetting = {
    languageModel: 'deepseek-v3-16k',
    imageGenMode: ImageGenMode.OFF,
    ttsId: this.character?.metadata.rita_voice_id || '',
    imageModel: 'kontext'
  };

  // Hint state
  private hintState: {
    isHintLoading: boolean;
    hints: string[];
    showHintPanel: boolean;
    canGenerateHint: boolean;
  } = {
    isHintLoading: false,
    hints: [],
    showHintPanel: false,
    canGenerateHint: true
  };

  // Subscribers for state changes
  private subscribers: Set<StateChangeCallback> = new Set();
  
  private constructor() {
    // Register image status callback for active image management
    registerImageStatusCallback('globalChatState', this.handleImageStatusChange.bind(this));
  }
  
  /**
   * Get singleton instance
   */
  public static getInstance(): GlobalChatState {
    if (!GlobalChatState.instance) {
      GlobalChatState.instance = new GlobalChatState();
    }
    return GlobalChatState.instance;
  }
  
  // =============================================================================
  // CHARACTER DATA METHODS
  // =============================================================================
  
  /**
   * Load character data based on params
   * @param locale Optional language code (e.g., 'en', 'zh-Hans'). If provided, loads localized fields from avatar_localized table.
   */
  public async loadCharacter(avatarId: string, 
    cachedCharacterMetadata: CharacterMetadata | null = null, 
    cachedCharacterChatHistory: CharacterChatHistory | null = null,
    preloadedPresignedUrl: boolean = false,
    locale?: string | null
  ): Promise<void> {
    // Clear inactivity timeout state and timer when loading a new character
    this.clearInactivityTimeoutState();
    this.clearInactivityTimer();
    
    this.setCharacterLoadingState({ isCharacterLoading: false, characterLoadingError: null });
    
    try {
      // const characterDataResult = await loadFullCharacterAction(resolvedParams.avatarId);
      let characterMetadataResult: { success: boolean; metadata: CharacterMetadata | null; 
                                     message: string } | null = null;
      let characterChatHistoryResult: { success: boolean; chatHistory: CharacterChatHistory | null; 
                                        message: string } | null = null;
      
      if (!cachedCharacterMetadata) {
        characterMetadataResult = await loadCharacterMetadataAction(avatarId, false, locale);
      } else {
        characterMetadataResult = { success: true, 
                                    metadata: cachedCharacterMetadata, 
                                    message: 'Cached metadata' };
      }

      // load active image from character metadata
      this.loadActiveImageFromCloudinaryPublicId(characterMetadataResult.metadata!.image_uri);

      if (!cachedCharacterChatHistory) {
        characterChatHistoryResult = await loadCharacterChatHistoryAction(avatarId);
        // characterChatHistoryResult = { success: false, 
        //   chatHistory: null, 
        //   message: 'Cached chat history' };
      } else {
        characterChatHistoryResult = { success: true, 
          chatHistory: cachedCharacterChatHistory, 
          message: 'Cached chat history' };
        // console.log('Cached chat history hit:', cachedCharacterChatHistory);
      }

      let chatHistory: CharacterChatHistory | undefined = undefined;
      if (characterChatHistoryResult.success && characterChatHistoryResult.chatHistory != null) {
        chatHistory = characterChatHistoryResult.chatHistory;
      }else{
        chatHistory = undefined;
      }
      const characterDataResult: Character = {
        metadata: characterMetadataResult.metadata!,
        chatHistory: chatHistory
      };
      this.character = characterDataResult;
      this.setTtsId(this.character?.metadata.rita_voice_id || '');
      this.setCharacterLoadingState({ 
        isCharacterLoading: false, 
        characterLoadingError: null 
      });
      
      // Initialize chat history in datahub first (async operation)
      const userEmail = userAppData.getUserEmail();
      await liveMessageDataHub.initChatHistory(this.character!, userEmail, avatarId);
      console.log('preloadedPresignedUrl :', preloadedPresignedUrl);

      this.notifySubscribers(); // Only notify when character data is loaded
      
    } catch (error) {
      console.error('Error loading character:', error);
      this.setCharacterLoadingState({ 
        isCharacterLoading: false, 
        characterLoadingError: 'Failed to load character' 
      });
      this.notifySubscribers(); // Only notify when error state is finalized
    }
  }
  
  /**
   * Get character data
   */
  public getCharacter(): Character | null {
    return this.character;
  }
  
  /**
   * Reset frontend chat history to default
   */
  public resetFrontendChatHistory(): void {
    if (!this.character) {
      return;
    }
    liveMessageDataHub.clearMessages();
    liveMessageDataHub.initDefaultHistory(this.character);
  }
  // =============================================================================
  // CHARACTER LOADING STATE METHODS
  // =============================================================================
  
  /**
   * Set character loading state
   */
  private setCharacterLoadingState(state: CharacterLoadingState): void {
    this.characterLoadingState = { ...state };
  }
  
  /**
   * Get character loading state
   */
  public getCharacterLoadingState(): CharacterLoadingState {
    return { ...this.characterLoadingState };
  }
  
  // =============================================================================
  // ACTIVE IMAGE METHODS
  // =============================================================================
  
  /**
   * Set active image from URI - public method for external use
   * Includes automatic head detection and image swap for live chat
   */
  public async setActiveImage(s3key: string): Promise<void> {
    // First load the image
    this.loadActiveImageFromCloudinaryPublicId(s3key);

    // Then perform head detection and image swap if in live mode
    try {
      await this.performHeadDetectionAndImageSwap(s3key);
    } catch (error) {
      console.error('Error during automatic head detection and image swap:', error);
      // Don't fail the image setting operation if head detection fails
    }
  }

  /**
   * Perform head detection and image swap if in live mode
   */
  private async performHeadDetectionAndImageSwap(s3key: string): Promise<void> {
    // Only perform image swap if we're in live chat and have a connected room
    if (this.chatStatus !== ChatStatus.LIVE || !this.room || this.room.state !== 'connected') {
      return;
    }

    try {
      // Get presigned URL for the image
      const presignedUrlResponse = await getPresignedUrl(s3key);
      const imageUrl = presignedUrlResponse.presignedUrl;

      // Fetch the image and convert to base64 for head detection
      const imageResponse = await fetch(imageUrl);
      const imageBlob = await imageResponse.blob();
      const base64Data = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.readAsDataURL(imageBlob);
      });

      // Send image data for head detection
      const { detectHead } = await import('@/app/lib/actions/head-detection');
      const headDetectionResult = await detectHead(base64Data, this.character?.metadata.avatar_id || '', false);

      if (headDetectionResult.success && headDetectionResult.data) {
        const { has_head, confidence_score, num_faces_detected } = headDetectionResult.data;

        console.log('Head detection result for active image change:', {
          has_head,
          confidence_score,
          num_faces_detected,
          s3key
        });

        // If has head, send image swap to backend
        if (has_head) {
          console.log('Active image passed head detection - sending image swap to backend');

          const swapData = {
            type: 'IMAGE_URL_SWAP',
            currentInUse: imageUrl,
            timestamp: new Date().toISOString()
          };

          await this.room.localParticipant.publishData(
            new TextEncoder().encode(JSON.stringify(swapData)),
            { topic: 'image_url_swap' }
          );
          console.log('!! sent image url swap for active image change: ', imageUrl);

          // Also send chat setting update with current settings and new active image URL
          const chatSettingData = {
            topic: 'user_setting',
            type: 'chat_setting',
            content: {
              llm_model: this.userChatSetting.languageModel,
              image_gen: this.userChatSetting.imageGenMode === 'auto' ? 'auto' : 'off',
              tts_id: this.userChatSetting.ttsId,
              active_image_url: s3key
            }
          };

          await this.room.localParticipant.publishData(
            new TextEncoder().encode(JSON.stringify(chatSettingData)),
            { reliable: true }
          );
          console.log('!! sent chat setting update with new active image URL: ', s3key);
        } else {
          console.log('Active image failed head detection - no image swap sent', {
            has_head,
            confidence_score,
            num_faces_detected
          });
        }
      } else {
        console.warn('Head detection failed for active image change:', headDetectionResult.error);
      }
    } catch (error) {
      console.error('Error during head detection for active image change:', error);
      // Don't fail the image setting operation if head detection fails
    }
  }
  
  /**
   * Load active image from Cloudinary public ID
   */
  private loadActiveImageFromCloudinaryPublicId(s3key: string): void {
    if (!s3key) return;

    // Set loading state (don't notify - this is intermediate state)
    this.activeImage.imageUri = s3key;
    this.activeImage.loadingStatus = LiveImageStatus.LOADING;
    this.activeImage.imageData = null;
    this.activeImage.imageRawData = null;
    
    // Start async loading (non-blocking)
    this.loadCloudinaryImageData(s3key, this.activeImage);
  }

  /**
   * Load profile image from Cloudinary public ID
   */
  private loadProfileImageFromCloudinaryPublicId(publicId: string): void {
    if (!publicId) return;

    // Set loading state (don't notify - this is intermediate state)
    this.profileImage.imageUri = publicId;
    this.profileImage.loadingStatus = LiveImageStatus.LOADING;
    this.profileImage.imageData = null;
    
    // Start async loading (non-blocking)
    this.loadCloudinaryImageData(publicId, this.profileImage);
  }
  
  /**
   * Async function to handle Cloudinary image loading
   */
  private async loadCloudinaryImageData(s3key: string, targetImage: ImageWithStatus): Promise<void> {
    try {
      // Ensure Cloudinary is initialized and time the operation
      // initCloudinaryWithPreloadedConfig('dango-cloud');

      // Create Cloudinary image object
      const publicId = extractPublicId(s3key);
      const cloudinaryImage = createCloudinaryImageSync(publicId);
      
      if (!cloudinaryImage) {
        throw new Error('Failed to create Cloudinary image');
      }
      
      // Only update if this is still the current public ID
      if (targetImage.imageUri === s3key) {
        targetImage.imageData = cloudinaryImage;
        targetImage.loadingStatus = LiveImageStatus.LOADED;
        this.notifySubscribers();
      }
      
    } catch (error) {
      console.error('Failed to load Cloudinary image:', error);
      if (targetImage.imageUri === s3key) {
        targetImage.loadingStatus = LiveImageStatus.FAILED;
        this.notifySubscribers();
      }
    }
  }
  
  /**
   * Get active image data
   */
  public getActiveImage(): ImageWithStatus {
    return { ...this.activeImage };
  }

  /**
   * Get profile image data
   */
  public getProfileImage(): ImageWithStatus {
    return { ...this.profileImage };
  }
  
  /**
   * Handle image status changes from the global image callback system
   */
  private handleImageStatusChange(messageId: string, status: LiveImageStatus, imageData?: any): void {
    // This could be used for coordinating with message image loading if needed
    // For now, we'll just log it for debugging
    console.debug('Image status change:', messageId, status, imageData ? 'with imageData' : 'no imageData');
  }
  
  // =============================================================================
  // CHAT STATUS METHODS
  // =============================================================================
  
  /**
   * Set chat status
   */
  public setChatStatus(status: ChatStatus): void {
    if (this.chatStatus !== status) {
      const previousStatus = this.chatStatus;
      this.chatStatus = status;
      
      // Handle inactivity timer based on status changes
      if (status === ChatStatus.LIVE) {
        // Start timer when entering LIVE status
        // console.log('[InactivityTimer] Chat status changed to LIVE - starting inactivity timer');
        this.resetInactivityTimer();
      } else if (previousStatus === ChatStatus.LIVE) {
        // Clear timer when leaving LIVE status
        // console.log('[InactivityTimer] Chat status changed from LIVE - clearing inactivity timer');
        this.clearInactivityTimer();
      }
      
      this.notifySubscribers();
    }
  }
  
  /**
   * Get chat status
   */
  public getChatStatus(): ChatStatus {
    return this.chatStatus;
  }

  /**
   * Get debug play back flag
   */
  public getDebugPlayBack(): boolean {
    return this.debugPlayBack;
  }

  /**
   * Set debug play back flag
   */
  public setDebugPlayBack(debugPlayBack: boolean): void {
    if (this.debugPlayBack !== debugPlayBack) {
      this.debugPlayBack = debugPlayBack;
      this.notifySubscribers();
    }
  }

  /**
   * Get debug auto launch video flag
   */
  public getDebugAutoLaunchVideo(): boolean {
    return this.debugAutoLaunchVideo;
  }

  /**
   * Set debug auto launch video flag
   */
  public setDebugAutoLaunchVideo(debugAutoLaunchVideo: boolean): void {
    if (this.debugAutoLaunchVideo !== debugAutoLaunchVideo) {
      this.debugAutoLaunchVideo = debugAutoLaunchVideo;
      this.notifySubscribers();
    }
  }

  // =============================================================================
  // CHAT UI STATE METHODS
  // =============================================================================
  
  /**
   * Set image generation state
   */
  public setIsImageGenerating(isGenerating: boolean): void {
    if (this.isImageGenerating !== isGenerating) {
      this.isImageGenerating = isGenerating;
      this.notifySubscribers();
    }
  }
  
  /**
   * Get image generation state
   */
  public getIsImageGenerating(): boolean {
    return this.isImageGenerating;
  }
  
  /**
   * Set streaming message ID
   */
  public setStreamingMessageId(messageId: string): void {
    if (this.streamingMessageId !== messageId) {
      this.streamingMessageId = messageId;
      this.notifySubscribers();
    }
  }
  
  /**
   * Get streaming message ID
   */
  public getStreamingMessageId(): string {
    return this.streamingMessageId;
  }
  
  /**
   * Set last processed index for transcription tracking
   */
  public setLastProcessedIndexForTranscriptionTracking(index: number): void {
    if (this.lastProcessedIndexForTranscriptionTracking !== index) {
      this.lastProcessedIndexForTranscriptionTracking = index;
      this.notifySubscribers();
    }
  }
  
  /**
   * Get last processed index for transcription tracking
   */
  public getLastProcessedIndexForTranscriptionTracking(): number {
    return this.lastProcessedIndexForTranscriptionTracking;
  }
  
  // =============================================================================
  // LIVEKIT ROOM STATE METHODS
  // =============================================================================
  
  /**
   * Set room
   */
  public setRoom(room: Room | null): void {
    if (this.room !== room) {
      this.room = room;
      this.notifySubscribers();
    }
  }
  
  /**
   * Get room
   */
  public getRoom(): Room | null {
    return this.room;
  }

  /**
   * Set preJoinChoices
   */
  public setPreJoinChoices(preJoinChoices: LocalUserChoices | undefined): void {
    if (this.preJoinChoices !== preJoinChoices) {
      this.preJoinChoices = preJoinChoices;
      this.notifySubscribers();
    }
  }
  
  /**
   * Get preJoinChoices
   */
  public getPreJoinChoices(): LocalUserChoices | undefined {
    return this.preJoinChoices;
  }
  
  /**
   * Set connectionDetails
   */
  public setConnectionDetails(connectionDetails: ConnectionDetails | undefined): void {
    if (this.connectionDetails !== connectionDetails) {
      this.connectionDetails = connectionDetails;
      this.notifySubscribers();
    }
  }
  
  /**
   * Get connectionDetails
   */
  public getConnectionDetails(): ConnectionDetails | undefined {
    return this.connectionDetails;
  }

  /**
   * Set isInitiatingRoom
   */
  public setIsInitiatingRoom(isInitiatingRoom: boolean): void {
    this.isInitiatingRoom = isInitiatingRoom;
    this.notifySubscribers();
  }

  /**
   * Get isInitiatingRoom
   */
  public getIsInitiatingRoom(): boolean {
    return this.isInitiatingRoom;
  }
  
  // =============================================================================
  // INACTIVITY TIMEOUT STATE METHODS
  // =============================================================================

  /**
   * Set inactivity timeout state
   */
  public setInactivityTimeoutState(state: {
    isInactivityTimeoutPopupOpen: boolean;
    inactivityTimeoutReason: 'server_signal' | 'user_inactivity' | null;
  }): void {
    if (this.inactivityTimeoutState !== state) {
      this.inactivityTimeoutState = { ...state };
      this.notifySubscribers();
    }
  }

  /**
   * Get inactivity timeout state
   */
  public getInactivityTimeoutState(): {
    isInactivityTimeoutPopupOpen: boolean;
    inactivityTimeoutReason: 'server_signal' | 'user_inactivity' | null;
  } {
    return { ...this.inactivityTimeoutState };
  }

  /**
   * Clear inactivity timeout state (useful for cleanup when switching characters)
   */
  public clearInactivityTimeoutState(): void {
    this.inactivityTimeoutState = {
      isInactivityTimeoutPopupOpen: false,
      inactivityTimeoutReason: null
    };
    this.notifySubscribers();
  }

  /**
   * Reset inactivity timer - clears existing timer and starts new one if in LIVE status
   * 
   * WHEN TO CALL:
   * - User interactions (typing, clicking buttons, sending messages)
   * - Do NOT call manually for chat status changes (handled automatically)
   * - Do NOT call in component cleanup (handled automatically)
   */
  public resetInactivityTimer(): void {
    // console.log('[InactivityTimer] Timer reset - restarting 5 minute countdown');
    
    // Clear existing timers
    if (this.inactivityTimeoutRef) {
      clearTimeout(this.inactivityTimeoutRef);
      this.inactivityTimeoutRef = null;
    }
    if (this.inactivityCountdownRef) {
      clearInterval(this.inactivityCountdownRef);
      this.inactivityCountdownRef = null;
    }
    
    // Only start timer if in LIVE chat status
    if (this.chatStatus === ChatStatus.LIVE) {
      this.inactivityStartTime = Date.now();
      
      // Start the main timeout timer
      this.inactivityTimeoutRef = setTimeout(() => {
        // console.log('[InactivityTimer] 5 minutes without user input - triggering timeout');
        
        // Clear countdown timer
        if (this.inactivityCountdownRef) {
          clearInterval(this.inactivityCountdownRef);
          this.inactivityCountdownRef = null;
        }
        
        // Set inactivity timeout state (components will react to this)
        this.setInactivityTimeoutState({
          isInactivityTimeoutPopupOpen: true,
          inactivityTimeoutReason: 'user_inactivity'
        });
      }, this.INACTIVITY_TIMEOUT_MS);
      
      // Start countdown logging (every second) - commented out
      // this.inactivityCountdownRef = setInterval(() => {
      //   const elapsed = Date.now() - this.inactivityStartTime;
      //   const remaining = Math.max(0, this.INACTIVITY_TIMEOUT_MS - elapsed);
      //   const remainingSeconds = Math.ceil(remaining / 1000);
      //   
      //   if (remainingSeconds > 0) {
      //     console.log(`[InactivityTimer] ${remainingSeconds} seconds remaining until timeout`);
      //   }
      // }, 1000);
      
      // console.log('[InactivityTimer] Started 5 minute (300 second) countdown');
    }
  }

  /**
   * Clear inactivity timer without starting a new one
   * 
   * WHEN TO CALL:
   * - Internal use only (called automatically by setChatStatus and reset)
   * - Do NOT call manually from components
   */
  public clearInactivityTimer(): void {
    // console.log('[InactivityTimer] Timer cleared');
    
    if (this.inactivityTimeoutRef) {
      clearTimeout(this.inactivityTimeoutRef);
      this.inactivityTimeoutRef = null;
    }
    if (this.inactivityCountdownRef) {
      clearInterval(this.inactivityCountdownRef);
      this.inactivityCountdownRef = null;
    }
  }
  
  // =============================================================================
  // AGENT SIGNAL METHODS
  // =============================================================================

  /**
   * Set agent avatar entered signal
   */
  public setAgentAvatarEntered(entered: boolean): void {
    this.agentAvatarEntered = entered;
    this.notifySubscribers();
  }

  /**
   * Get agent avatar entered signal
   */
  public getAgentAvatarEntered(): boolean {
    return this.agentAvatarEntered;
  }

  /**
   * Check if agent avatar signal has been received
   */
  public hasAgentAvatar(): boolean {
    return this.agentAvatarEntered;
  }

  /**
   * Reset agent signals (useful for cleanup)
   */
  public resetAgentSignals(): void {
    this.agentAvatarEntered = false;
    this.agentAsrEntered = false;
    this.notifySubscribers();
  }

  // =============================================================================
  // HINT STATE METHODS
  // =============================================================================

  public setHintLoading(isLoading: boolean): void {
    this.hintState.isHintLoading = isLoading;
    this.notifySubscribers();
  }

  public getHintLoading(): boolean {
    return this.hintState.isHintLoading;
  }

  public setHints(hints: string[]): void {
    this.hintState.hints = hints;
    this.notifySubscribers();
  }

  public getHints(): string[] {
    return this.hintState.hints;
  }

  public setShowHintPanel(show: boolean): void {
    this.hintState.showHintPanel = show;
    this.notifySubscribers();
  }

  public getShowHintPanel(): boolean {
    return this.hintState.showHintPanel;
  }

  public setCanGenerateHint(canGenerate: boolean): void {
    this.hintState.canGenerateHint = canGenerate;
    this.notifySubscribers();
  }

  public getCanGenerateHint(): boolean {
    return this.hintState.canGenerateHint;
  }

  public resetHintState(): void {
    this.hintState = {
      isHintLoading: false,
      hints: [],
      showHintPanel: false,
      canGenerateHint: true
    };
    this.notifySubscribers();
  }

  // =============================================================================
  // USER CHAT SETTINGS METHODS
  // =============================================================================

  /**
   * Get user chat settings
   */
  public getUserChatSetting(): UserChatSetting {
    return { ...this.userChatSetting };
  }

  /**
   * Set user chat settings
   */
  public setUserChatSetting(settings: UserChatSetting): void {
    if (JSON.stringify(this.userChatSetting) !== JSON.stringify(settings)) {
      this.userChatSetting = { ...settings };
      this.notifySubscribers();
    }
  }

  /**
   * Update language model
   */
  public setLanguageModel(languageModel: string): void {
    if (this.userChatSetting.languageModel !== languageModel) {
      this.userChatSetting.languageModel = languageModel;
      this.notifySubscribers();
    }
  }

  /**
   * Update image generation mode
   */
  public setImageGenMode(imageGenMode: ImageGenMode): void {
    if (this.userChatSetting.imageGenMode !== imageGenMode) {
      this.userChatSetting.imageGenMode = imageGenMode;
      this.notifySubscribers();
    }
  }

  /**
   * Update TTS ID
   */
  public setTtsId(ttsId: string): void {
    if (this.userChatSetting.ttsId !== ttsId) {
      this.userChatSetting.ttsId = ttsId;
      this.notifySubscribers();
    }
  }

  /**
   * Update image generation model
   */
  public setImageModel(imageModel: string): void {
    if (this.userChatSetting.imageModel !== imageModel) {
      this.userChatSetting.imageModel = imageModel;
      this.notifySubscribers();
    }
  }

  /**
   * Set agent ASR entered signal
   */
  public setAgentAsrEntered(entered: boolean): void {
    this.agentAsrEntered = entered;
    this.notifySubscribers();
  }

  /**
   * Get agent ASR entered signal
   */
  public getAgentAsrEntered(): boolean {
    return this.agentAsrEntered;
  }

  /**
   * Check if agent ASR signal has been received
   */
  public hasAgentAsr(): boolean {
    return this.agentAsrEntered;
  }
  
  // =============================================================================
  // SUBSCRIPTION METHODS
  // =============================================================================
  
  /**
   * Subscribe to state changes
   */
  public subscribe(callback: StateChangeCallback): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }
  
  /**
   * Notify all subscribers of state changes
   */
  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback());
  }
  
  // =============================================================================
  // UTILITY METHODS
  // =============================================================================
  
  /**
   * Reset all state (useful for testing or cleanup)
   */
  public reset(): void {
    liveMessageDataHub.clearMessages();
    this.character = null;
    this.characterLoadingState = {
      isCharacterLoading: false,
      characterLoadingError: null
    };
    this.activeImage = {
      imageUri: null,
      imageData: null,
      imageRawData: null,
      loadingStatus: LiveImageStatus.NOT_LOADED
    };
    this.chatStatus = ChatStatus.INFO;
    this.isImageGenerating = false;
    this.streamingMessageId = '';
    this.lastProcessedIndexForTranscriptionTracking = -1;
    // Reset LiveKit room state
    this.room = null;
    this.preJoinChoices = undefined;
    this.connectionDetails = undefined;
    this.isInitiatingRoom = false;
    this.inactivityTimeoutState = {
      isInactivityTimeoutPopupOpen: false,
      inactivityTimeoutReason: null
    };
    // Clear inactivity timer
    this.clearInactivityTimer();
    this.inactivityStartTime = 0;
    // Reset agent signals
    this.agentAvatarEntered = false;

    // Reset hint state
    this.hintState = {
      isHintLoading: false,
      hints: [],
      showHintPanel: false,
      canGenerateHint: true
    };
    // Reset user chat settings to defaults
    this.userChatSetting = {
      languageModel: 'deepseek-v3-16k',
      imageGenMode: ImageGenMode.OFF,
      ttsId: '',
      imageModel: 'kontext'
    };
    // Reset debug flags to defaults
    this.debugAutoLaunchVideo = false;
    this.agentAsrEntered = false;
    this.notifySubscribers();
  }
}

// Export singleton instance
export const globalChatState = GlobalChatState.getInstance(); 